import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { generateClassName } from '@klv/shared/utils/functions';
import { useTranslation } from '@klv/hooks/useTranslation';
import { InfoToolTip } from './info-tooltip';
import * as styles from './styles/slider.module.css';
import type { ISlider } from './types';

const Slider = ({
  className = '',
  range,
  headline,
  subText,
  headlineSubInfo,
  subTextInfo,
  subTextInfoHeader,
  italicizedSubText,
  value, // Controlled value from parent
  onChange,
  variant,
  ...props
}: ISlider) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  
  const platformCard = variant === 'platform';
  
  const maxSteps = range.length - 1;
  
  // Find the slider position based on the controlled value
  const sliderValue = range.findIndex(item => item.value === value);
  const [labelHeight, setLabelHeight] = useState(0);
  const labelRef = useRef<HTMLDivElement>(null);

  // Handle label height updates
  useEffect(() => {
    const updateLabelHeight = () => {
      if (labelRef.current) setLabelHeight(labelRef.current.offsetHeight);
    };
    
    updateLabelHeight();
    window.addEventListener('resize', updateLabelHeight);
    return () => window.removeEventListener('resize', updateLabelHeight);
  }, []);

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = Number(e.target.value);
      const newSliderValue = Math.round(rawValue);
      const closestRangeValue = range[newSliderValue].value;
      
      if (onChange) onChange(closestRangeValue);
    },
    [range, onChange],
  );

  const trackBackground = useMemo(() => {
    const percentage = (sliderValue / maxSteps) * 100;
    return `linear-gradient(to right, #744A6E ${percentage}%, var(--color-charcoal) ${percentage}%)`;
  }, [sliderValue, maxSteps]);

  const scrollPosition = sliderValue !== -1 ? sliderValue * labelHeight * -1 : 0;

  return (
    <div className={generateClassName([className, styles.slider])} {...props}>
      <h6 className={`h6 ${styles.headline}`}>{headline}</h6>
      {italicizedSubText && <div className={styles.italicizedSubText}>{italicizedSubText}</div>}
      {headlineSubInfo && <div className={styles.headlineSubInfo}>{headlineSubInfo}</div>}
      <div className={platformCard ? styles.platformSubTextContainer : styles.subTextContainer}>
        <InfoToolTip
          subText={subText}
          subTextInfoHeader={subTextInfoHeader}
          subTextInfo={subTextInfo}
          variant={platformCard ? 'platform' : 'default'}
        />
        <div className={platformCard ? styles.platformRangeLabelContainer : styles.rangeLabelContainer}>
          <div
            className={styles.rangeLabelList}
            style={{ transform: `translateY(${scrollPosition}px)` }}
          >
            {range.map((item, index) => (
              <div
                key={index}
                ref={index === 0 ? labelRef : null}
                className={platformCard ? styles.platformRange : styles.rangeLabel}
              >
                {item.label.split(' – ').map((num, i) => {
                  const cleanedNum = num.replace(/[^\d-]/g, '');
                  const formattedNum = new Intl.NumberFormat(locale).format(Number(cleanedNum));
                  return i === 0 ? formattedNum : ` – ${formattedNum}`;
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={maxSteps}
        step={0.01}
        value={sliderValue}
        onChange={handleSliderChange}
        className={styles.customSlider}
        style={{ background: trackBackground }}
        title="Slider"
      />
    </div>
  );
};

export default Slider;
